import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Sun, Wind, Droplets, Leaf } from "lucide-react";
import { motion } from "motion/react";

export function RenewableEnergySources() {
  const sources = [
    {
      name: "Solar Energy",
      icon: Sun,
      image: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsJTIwZmFybXxlbnwxfHx8fDE3NjQ0MTM4MDR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      capacity: "5 MW",
      gradient: "from-yellow-500 to-orange-500"
    },
    {
      name: "Wind Energy",
      icon: Wind,
      image: "https://images.unsplash.com/photo-1566933971571-e3cb6990e9f8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3aW5kJTIwdHVyYmluZSUyMGZpZWxkfGVufDF8fHx8MTc2NDMyMTQxMXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      capacity: "3 MW",
      gradient: "from-cyan-500 to-blue-500"
    },
    {
      name: "Hydro Power",
      icon: Droplets,
      image: "https://images.unsplash.com/photo-1733001523587-ad1bc6d025c1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoeWRyb2VsZWN0cmljJTIwZGFtJTIwcG93ZXJ8ZW58MXx8fHwxNzY0NDEzODA0fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      capacity: "0 MW",
      gradient: "from-blue-500 to-indigo-500"
    },
    {
      name: "Biomass Energy",
      icon: Leaf,
      image: "https://images.unsplash.com/photo-1747345341772-7516f6b54958?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiaW9tYXNzJTIwZW5lcmd5JTIwcGxhbnR8ZW58MXx8fHwxNzY0NDEzODA1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      capacity: " 0 MW",
      gradient: "from-emerald-500 to-green-600"
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl mb-4 bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
            Our Renewable Energy Network
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Connected to multiple verified renewable energy sources across the region
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sources.map((source, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <ImageWithFallback
                  src={source.image}
                  alt={source.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                
                {/* Icon Badge */}
                <div className={`absolute top-4 right-4 w-12 h-12 bg-gradient-to-br ${source.gradient} rounded-full flex items-center justify-center shadow-lg`}>
                  <source.icon className="w-6 h-6 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-xl mb-2">{source.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-sm opacity-90">Installed Capacity</span>
                  <span className="text-sm px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                    {source.capacity}
                  </span>
                </div>
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 to-cyan-500/0 group-hover:from-emerald-500/20 group-hover:to-cyan-500/20 transition-all duration-300"></div>
            </motion.div>
          ))}
        </div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-2xl p-8 text-white"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl md:text-4xl mb-2">8</p>
              <p className="text-sm md:text-base opacity-90">Total MW Capacity</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl mb-2">93%</p>
              <p className="text-sm md:text-base opacity-90">Clean Energy Mix</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl mb-2">24/7</p>
              <p className="text-sm md:text-base opacity-90">Continuous Supply</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl mb-2">100%</p>
              <p className="text-sm md:text-base opacity-90">Verified Sources</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
